import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import addDays from 'date-fns/addDays';

import { API } from '../../API';
import { ampli } from "../../ampli";
import { useCurrentUser } from '../../contexts/CurrentUser';
import { showRequestError } from '../../utils/requestError';
import Switch from '../Switch';
import { ReactComponent as CopyIcon } from '../../assets/copy.svg';
import { Tooltip } from 'react-tooltip';
import { toast } from 'react-toastify';
import './index.css';

// Share-link form for a report, a case (narrative) or a custom dashboard.
// Exactly one of reportID / narrativeId / customDashboardId is expected.
export default function Share({ isVisible, onClose, reportID, narrativeId, customDashboardId }) {
  const { t } = useTranslation();
  const options = [
    { value: addDays(new Date(), 7), label: t('One week') },
    { value: addDays(new Date(), 30), label: t('Month') },
    { value: addDays(new Date(), 90), label: t('Three Months') },
  ]

  
  const [currentUser] = useCurrentUser();
  const [showExpiration, setShowExpiration] = useState(false);
  const [expiration, setExpiration] = useState(options[0]);
  const [, setShare] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [copyButton, setCopyButton] = useState({
    icon: '/static/copy.svg',
    title: t('Create link'),
  });
  const [values, setValues] = useState({
    password: '',
  });

  const [shareLink, setShareLink] = useState('');

  const handleChange = (e) => {
    setValues((oldValues) => ({
      ...oldValues,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShareLink('')

    const data = {
      password: values.password,
      expires: (showExpiration && expiration ) ? expiration?.value : addDays(new Date(), 365 * 2),
    };

    let loginPath;
    if (reportID) {
      data['report_id'] = reportID
      loginPath = `/reports/${reportID}/share/login`
    } else if (narrativeId) {
      data['narrative_id'] = narrativeId
      loginPath = `/narratives/${narrativeId}/share/login`
    } else if (customDashboardId) {
      data['custom_dashboard_id'] = customDashboardId
      loginPath = `/custom-dashboards/${customDashboardId}/share/login`
    } else {
      console.warn('Neither reportID, narrativeId nor customDashboardId provided')
      return
    }

    setIsLoading(true)
    setCopyButton({ icon: '/static/copied.svg', title: t('Loading...1') });

    API.fetch('POST', `/API/v1/reports/shares`, null, data).then((data) => {
      setIsLoading(false);

      const shareLoginURL = `${window.location.protocol}//${window.location.host}${loginPath}?h=${data.hash}&with_password=${data.with_password}`

      // navigator.clipboard.writeText(shareLoginURL);
      setShareLink(shareLoginURL);
      setShare(data);

      setCopyButton({ icon: '/static/copy.svg', title: t('Create link') });

      if (narrativeId) {
        ampli.track({
          event_type: 'Share case',
          event_properties: {
            user_id: currentUser?.id,
            workspace_id: currentUser?.workspace_id,
            narrative_id: narrativeId,
          }
        });
      }
      if (reportID) {
        ampli.track({
          event_type: 'Share report',
          event_properties: {
            user_id: currentUser?.id,
            workspace_id: currentUser?.workspace_id,
            report_id: reportID,
          }
        });
      }
      if (customDashboardId) {
        ampli.track({
          event_type: 'Share custom dashboard',
          event_properties: {
            user_id: currentUser?.id,
            workspace_id: currentUser?.workspace_id,
            dashboard_id: customDashboardId,
          }
        });
      }
    }).catch((error) => {
      setIsLoading(false);
      setCopyButton({ icon: '/static/copy.svg', title: t('Create link') });
      showRequestError(error, t('Share link'));
    });
  };

  const linkHelp = customDashboardId
    ? t('Copy the link to the dashboard')
    : t('Copy the link to the report');

  return (
    <>
      <div className="option">
        <div>
          <div className="title">{t('Validity period')}</div>
          <div className="help">
            {t('Make sure this link stops working after certain period of time')}
          </div>
        </div>
        <div>
          <Switch
            value={showExpiration}
            onChange={(v) => setShowExpiration(v)}
          />
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        {showExpiration ? (
          <div className="option">
            <div className="help">{t('Select a link duration period')}</div>
            <div>
              <Select
                onChange={(value) => setExpiration(value)}
                value={expiration}
                isSearchable={false}
                name='expiration'
                type='text'
                isClearable={false}
                placeholder={t('Select duration...')}
                options={options}
              />
            </div>
          </div>
        ) : (
          ''
        )}
        <div className="option">
          <div>
            <div className="title">{t('Password')}</div>
            <div className="help">{t('Set password for the share link')}</div>
          </div>
          <div className="form-element">
            <input
              type="password"
              name="password"
              onChange={handleChange}
              autocomplete="new-password"
            />
          </div>
        </div>
        <div className="option">
          <div>
            <div className="title">{t('Link')}</div>
            <div className="help">{linkHelp}</div>
          </div>
          {shareLink ? <div className="form-element">
            <input
              type='text'
              defaultValue={shareLink}
              className='share-input'
            />
          </div> : ''}
          
          <div>
            {!shareLink ? <button className='copy-button' disabled={isLoading}>
              <img src={copyButton.icon} alt="Copy icon" />
              {copyButton.title}
            </button> : ''}
            {shareLink 
              ? <button 
                  data-tooltip-id="copy-link"
                  data-tooltip-content={t("Copy link")}
                  className='copy-button'
                  onClick={(e) => {
                    e.preventDefault();
                    // the clipboard API is missing on insecure origins and can reject, so
                    // both outcomes get a toast: the click has no other visible effect
                    if (!navigator?.clipboard?.writeText) {
                      toast.error(t('Could not copy the link. Please copy it manually.'));
                      return;
                    }
                    navigator.clipboard.writeText(shareLink)
                      .then(() => toast.success(t('Link copied')))
                      .catch(() => toast.error(t('Could not copy the link. Please copy it manually.')));
                }}>
                  <CopyIcon/>
                </button> : ''}
                <Tooltip id="copy-link" place="bottom"/>
          </div>
        </div>
      </form>
    </>
  );
}
